import {
  createContext,
  PropsWithChildren,
  useContext,
  useEffect,
  useState,
} from "react";
import apiRequest from "../axios/useApi";
import { usePlaidLink } from "react-plaid-link";
import { UIContext } from "./UIProvider";
import {
  BookEZAccount,
  LinkedAccountsContextType,
  PlaidAccountItems,
  PlaidAccounts,
  UserAccount,
} from "../pages/Banking/BankingInterface";
import { handleApiError } from "../lib/utils";
import { format } from "date-fns";
import { BankingContext } from "./BankingProvider";

type Props = PropsWithChildren;

export const LinkedAccountsContext = createContext<LinkedAccountsContextType>({
  refetchAccounts: () => Promise.resolve(),
  open: () => {},
  ready: false,
  linkToken: "",
  userAccounts: [],
  bookEZAccounts: [],
  plaidAccounts: [],
  setIsLinkedModalOpen: () => {},
  isLinkedModalOpen: false,
  selectedBookEZAccount: null,
  selectedPlaidAccount: null,
  handleLinkAccounts: () => {},
  setSelectedBookEZAccount: () => {},
  setSelectedPlaidAccount: () => {},
  HandleOpenLinkModal: () => {},
  selectedAccountId: null,
  toggleAccountSelection: (accountId: string) => {},
  handleSyncTransaction: (account_id: string) => {},
  getLinkToken: () => {},
  clickedSyncID: null,
  setClickedSyncID: () => {},
});

export const LinkedAccountProvider = ({ children }: Props) => {
  const {
    onError,
    onSuccess,
    businessId,
    plaidClientUserId,
    plaidClientUserName,
  } = useContext(UIContext);
  const [loadingStates, setLoadingStates] = useState({
    plaidAccounts: false,
    bookEZAccounts: false,
    accounts: false,
    linkToken: false,
    transactions: false,
    linkModal: false,
    createAccount: false,
    isSyncingTransactions: false,
  });

  const [linkToken, setLinkToken] = useState("");
  const [selectedAccountId, setSelectedAccountId] = useState<string | null>(
    null
  );
  const [clickedSyncID, setClickedSyncID] = useState<string | null>(null);

  const [userAccounts, setUserAccounts] = useState<UserAccount[]>([]);

  const [plaidAccounts, setPlaidAccounts] = useState<PlaidAccounts[]>([]);
  const [bookEZAccounts, setBookEZAccounts] = useState<BookEZAccount[]>([]);

  const [selectedBookEZAccount, setSelectedBookEZAccount] = useState<
    string | null
  >(null);
  const [selectedPlaidAccount, setSelectedPlaidAccount] = useState<
    string | null
  >(null);
  const [isLinkedModalOpen, setIsLinkedModalOpen] = useState(false);

  const handleLinkAccounts = async () => {
    if (selectedBookEZAccount && selectedPlaidAccount) {
      await CreateBookEZAccount(selectedPlaidAccount, selectedBookEZAccount);
      // onLinkAccounts(selectedBookEZAccount, selectedPlaidAccount)
      // Reset selections
    }
  };

  const getLinkToken = async () => {
    if (!plaidClientUserId || !plaidClientUserName) {
      onError?.(new Error("Plaid Client User ID and Name are required."));
      return;
    }
    setLoadingStates((prev) => ({ ...prev, linkToken: true }));
    const queryParams = new URLSearchParams(window.location.search);
    queryParams.append("ClientUserId", plaidClientUserId);
    queryParams.append("ClientName", plaidClientUserName);
    try {
      const response = await apiRequest<{
        success: boolean;
        data: string;
      }>({
        url: `/Plaid/GetPlaidLinkToken?${queryParams}`,
        method: "GET",
      });

      if (response && response.success && response?.data) {
        onSuccess?.(
          "GET",
          "getLinkToken",
          response?.message || "Link Token Fetched Successfully"
        );
        setLinkToken(response.data.toString());
      } else {
        handleApiError(response, onError);
      }
    } catch (error) {
      onError?.(new Error("An error occurred while fetching link token."));
    } finally {
      setLoadingStates((prev) => ({ ...prev, linkToken: false }));
    }
  };

  const CreateBookEZAccount = async (
    plaidAccountsId: string,
    internalAccountId: string
  ) => {
    setLoadingStates((prev) => ({ ...prev, createAccount: true }));
    const queryParams = new URLSearchParams(window.location.search);
    queryParams.append("PlaidAccountsId", plaidAccountsId);
    queryParams.append("InternalAccountId", internalAccountId);
    try {
      const response = await apiRequest<{
        success: boolean;
        data: string;
        errorDetail: {};
      }>({
        url: `/Plaid/${businessId}/CreatePlaidAccounts?${queryParams}`,
        method: "POST",
      });

      if (response && response.success) {
        onSuccess?.(
          "POST",
          "CreateBookEZAccount",
          response?.message || "Account Created Successfully"
        );
        getUserAccountsList();
      } else {
        handleApiError(response, onError);
      }
    } catch (error) {
      handleApiError(error as any, onError);
    } finally {
      setSelectedBookEZAccount(null);
      setSelectedPlaidAccount(null);
      setIsLinkedModalOpen(false);
      setLoadingStates((prev) => ({ ...prev, createAccount: false }));
    }
  };

  const getPlaidAccountsList = async () => {
    setLoadingStates((prev) => ({ ...prev, plaidAccounts: true }));
    try {
      const response = await apiRequest<{
        accounts: PlaidAccounts[];
        isSuccessStatusCode: boolean;
        error: string;
        payment_risk_assessment: string;
        request_id: string;
        items: PlaidAccountItems;
      }>({
        url: `/Plaid/${businessId}/GetPlaidAccounts`,
        method: "GET",
      });
      if (response && response.success && response.data) {
        onSuccess?.(
          "GET",
          "getPlaidAccounts",
          response?.message || "Plaid Accounts Fetched Successfully"
        );
        setPlaidAccounts(response.data.accounts || []);
      } else {
        handleApiError(response, onError);
      }
    } catch (error) {
      handleApiError(error as any, onError);
    } finally {
      setLoadingStates((prev) => ({ ...prev, plaidAccounts: false }));
    }
  };

  const getBookEZAccountsList = async () => {
    setLoadingStates((prev) => ({ ...prev, bookEZAccounts: true }));
    const queryParams = new URLSearchParams({
      AccountCategoryId: "18",
      PageSize: "25",
    });
    try {
      const response = await apiRequest<{ items: BookEZAccount[] }>({
        url: `/account/${businessId}/List/1?${queryParams}`,
        method: "GET",
      });
      if (response && response.success && response.data) {
        onSuccess?.(
          "GET",
          "getBookEZAccountsList",
          response?.message || "Book EZ Accounts Fetched Successfully"
        );
        setBookEZAccounts(response?.data?.items || []);
      } else {
        handleApiError(response, onError);
      }
    } catch (error) {
    } finally {
      setLoadingStates((prev) => ({ ...prev, bookEZAccounts: false }));
    }
  };

  const getUserAccountsList = async () => {
    setLoadingStates((prev) => ({ ...prev, accounts: true }));
    try {
      const response = await apiRequest<UserAccount[]>({
        url: `/Plaid/${businessId}/GetAccounts`,
        method: "GET",
      });
      if (response && response.success && response.data) {
        onSuccess?.(
          "GET",
          "getUserAccountsList",
          response?.message || "Accounts Fetched Successfully"
        );
        if (!selectedAccountId) {
          setSelectedAccountId(response.data[0]?.plaidAccountId);
        }
        setUserAccounts(response.data || []);
      } else {
        handleApiError(response);
      }
    } catch (error) {
      onError?.(new Error("An error occurred while fetching accounts."));
    } finally {
      setLoadingStates((prev) => ({ ...prev, accounts: false }));
    }
  };

  const syncAccountsTransactions = async (account_id: string) => {
    setLoadingStates((prev) => ({ ...prev, isSyncingTransactions: true }));
    const today = new Date();
    const startOfYear = new Date(today.getFullYear(), 0, 1); // Month is 0-indexed
    try {
      const response = await apiRequest<any>({
        url: `/Plaid/${businessId}/SyncPlaidTransaction`,
        method: "POST",
        data: {
          accountId: account_id,
          startDate: format(startOfYear, "yyyy-MM-dd"), // "2025-01-01"
          endDate: format(today, "yyyy-MM-dd"),
        },
      });
      if (response && response.success) {
        setSelectedAccountId(null);
        onSuccess?.(
          "POST",
          "syncAccountsTransactions",
          response?.message || "Account Synced successfully!"
        );
        await getUserAccountsList();
        setSelectedAccountId(account_id);
      } else {
        handleApiError(response, onError);
      }
    } catch (error) {
      onError?.(new Error("An error occurred while Syncing accounts."));
    } finally {
      setLoadingStates((prev) => ({ ...prev, isSyncingTransactions: false }));
    }
  };

  const handleSyncTransaction = (account_id: string) => {
    setClickedSyncID(account_id);
    syncAccountsTransactions(account_id);
  };

  const toggleAccountSelection = (accountId: string) => {
    setSelectedAccountId(accountId);
  };

  useEffect(() => {
    getUserAccountsList();
    getBookEZAccountsList();
    if (plaidClientUserId && plaidClientUserName) {
      getPlaidAccountsList();
    }
  }, []);

  const exchangePublicToken = async (public_token: string) => {
    //* api call for sending public token to Be and again fetch all account and transactions

    const response = await apiRequest<any>({
      url: `/Plaid/${businessId}/GenerateAccessToken/${public_token}`,
      method: "GET",
    });

    if (response.success) {
      onSuccess?.(
        "GET",
        "exchangePublicToken",
        response?.message || "Access Token Generated Successfully"
      );
      getUserAccountsList();
      getPlaidAccountsList();
    } else {
      handleApiError(response, onError);
    }
  };

  // STEP 3: exchange public token
  const { open, ready } = usePlaidLink({
    token: linkToken,
    onSuccess: (public_token) => {
      exchangePublicToken(public_token);
    },
    onExit: (err, metadata) => {
      if (err) {
        onError?.(new Error("An error occurred while fetching accounts."));
      }
    },
  });

  const refetchAccounts = () => {
    getUserAccountsList();
  };

  const HandleOpenLinkModal = (openState: boolean) => {
    if (openState === true) {
    } else {
      setSelectedBookEZAccount(null);
      setSelectedPlaidAccount(null);
      // Reset all loading states when modal closes
    }
    setIsLinkedModalOpen(openState);
  };

  return (
    <LinkedAccountsContext.Provider
      value={{
        loadingStates,
        refetchAccounts: async () => refetchAccounts(),
        open,
        ready,
        linkToken,
        userAccounts,
        plaidAccounts,
        bookEZAccounts,
        setIsLinkedModalOpen,
        isLinkedModalOpen,
        selectedBookEZAccount,
        selectedPlaidAccount,
        handleLinkAccounts,
        setSelectedBookEZAccount,
        setSelectedPlaidAccount,
        HandleOpenLinkModal,
        selectedAccountId,
        toggleAccountSelection,
        handleSyncTransaction,
        getLinkToken,
        clickedSyncID,
        setClickedSyncID,
      }}
    >
      {children}
    </LinkedAccountsContext.Provider>
  );
};
